import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, inject, viewChild } from "@angular/core";
import { ActivatedRoute, convertToParamMap, ParamMap, Params, Router, RouterLink } from "@angular/router";
import { Subscription } from "rxjs";
import swal from "sweetalert2";

import { AuthService } from "src/app/security/auth.service";
import { Menu } from "src/app/shared/model/security/menu.model";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  FormMantenimientoComponent,
  MantenimientoEnviado,
} from "src/app/mantenimiento/form-mantenimiento/form-mantenimiento.component";
import { MisMantenimientoComponent } from "src/app/mantenimiento/mis-mantenimiento/mis-mantenimiento.component";
import {
  PestanaSolicitudIa,
  SolicitudIaComponent,
  SolicitudIaEnviada,
} from "src/app/solicitudes-ia/solicitud-ia/solicitud-ia.component";
import { EstadoMantenimientoService } from "src/app/mantenimiento-modulos/estado-mantenimiento.service";
import { CLAVE_IA, CLAVE_MANTENIMIENTO, claveTicket, textoAviso } from "src/app/mantenimiento-modulos/reglas";
import { ModuloEnMantenimiento } from "src/app/shared/model/mantenimiento-modulos/mantenimiento-modulos.model";
import { LanzadorTickets, TIPOS_TICKET } from "./lanzador-tickets";
import { CONSULTANDO, Marca, SeguimientoSolicitudes, TipoSolicitud, marca } from "./seguimiento";

/** Lo que dice el encabezado en las tarjetas (y en una lista sin su ayuda). */
const AYUDA_INICIO = "Escoge qué necesitas y te llevamos a su formulario.";

/** Lo que se pide y se sigue aquí mismo (los tickets se llenan y se siguen en su módulo). */
export type Formulario = "ia" | "mantenimiento";
/**
 * Dónde está el concentrador: las tarjetas, el formulario de IA o de
 * Mantenimiento, el «Listo» después de enviar, o la lista de lo que uno ya
 * pidió de un tipo.
 */
export type Paso = "tipo" | "form" | "listo" | "mias";

/** Una tarjeta de «¿Qué necesitas?»: qué se pide con ella y su color. */
interface Tarjeta {
  tono: string;
  titulo: string;
  descripcion: string;
  ejemplos: string[];
  icono: string;
  accion: string;
  /** Solo en las de Tickets: el tipo que abre, con su id del módulo de Tickets. */
  idTicket?: number;
}

/** Una tarjeta ya en pantalla, con el proceso al que pertenece. */
interface TarjetaVisible extends Tarjeta {
  tipo: TipoSolicitud;
}

/** Uno de los tres procesos que se piden aquí. */
interface Opcion {
  id: TipoSolicitud;
  /** Sus tarjetas: Tickets ofrece una por cada tipo de ticket; IA y Mantenimiento, una. */
  tarjetas: Tarjeta[];
  /** El icono de su acceso en «¿Ya pediste algo?» y del encabezado de su lista. */
  icono: string;
  /** El factory del menú que da el permiso: sin él, sus tarjetas no salen. */
  factory: string;
  /**
   * Dónde ve uno lo que ya pidió: en su módulo (`ruta`, Tickets) o aquí
   * mismo (`ver`, IA y Mantenimiento).
   */
  mias: { texto: string; ayuda?: string; ruta?: string[]; ver?: Formulario };
}

/** Lo que se enseña en «Listo». */
export interface Resultado {
  tipo: Formulario;
  idSolicitud: number;
  /** Vacío si no se alcanzó a saber (IA, con la lista caída). */
  folio: string;
  /** Archivos que se subieron con ella (IA). */
  archivos: number;
  /** Fotos que no se pudieron subir (Mantenimiento). */
  fotosFallidas: string[];
}

/**
 * «Nueva solicitud»: un solo lugar para pedir, con un asistente que
 * primero deja escoger QUÉ se necesita y después abre SU proceso.
 *
 * Pedido de Norman (29/09/2026): integrar Tickets, Solicitar IA y
 * Mantenimiento. Y el 12/10/2026: un solo módulo para pedir Y seguir, sin
 * saltar a otros:
 *   - Los cuatro tipos de ticket son tarjetas al mismo nivel que IA y
 *     Mantenimiento (desde el 13/10/2026; antes iban en un paso aparte,
 *     tras la tarjeta «Soporte y sistemas»). Cada una abre SU formulario
 *     de siempre con LanzadorTickets, sin tocar el módulo de Tickets, y
 *     «Mis tickets» sigue en su módulo.
 *   - IA se pide aquí mismo con SU componente (SolicitudIaComponent:
 *     mismas validaciones, mismo DAR, mismo reintento), y «Mis solicitudes
 *     de IA» es su otra vista, también aquí.
 *   - Mantenimiento se llena aquí mismo en tres pasos, y «Mis solicitudes
 *     de mantenimiento» es MisMantenimientoComponent.
 *
 * Debajo de las tarjetas, «¿Ya pediste algo?» lleva a lo ya pedido de cada
 * tipo y dice cómo va: cuántas siguen en curso y cuántas esperan algo de
 * uno (SeguimientoSolicitudes). Ese último aviso es un enlace aparte, que
 * lleva directo a la que espera.
 *
 * La URL dice dónde se está, para que F5 y «atrás» no pierdan el lugar:
 * ?tipo=ia|mantenimiento es el paso 2 (su formulario) y
 * ?ver=ia|mantenimiento la
 * lista de lo pedido (con ?solicitud=12, el detalle de esa); «Listo» deja
 * ?enviada=<folio>, que en un F5 vuelve a las tarjetas. Las rutas de
 * antes (/admin/solicitudes-ia/solicitud y /admin/mantenimiento/solicitud)
 * llevan aquí (rutas-viejas.ts).
 *
 * NO TIENE PERMISOS PROPIOS MÁS ALLÁ DE SU MENÚ: cada tarjeta sale solo si
 * el usuario tiene el menú de ese proceso (como la página de
 * Configuración), y el servidor de cada uno lo vuelve a revisar.
 */
@Component({
  selector: "app-nueva-solicitud",
  standalone: true,
  templateUrl: "./nueva-solicitud.component.html",
  styleUrls: ["../../mantenimiento/mantenimiento-comun.css", "./nueva-solicitud.component.css"],
  imports: [CommonModule, RouterLink, FormMantenimientoComponent, MisMantenimientoComponent, SolicitudIaComponent],
  providers: [LanzadorTickets, SeguimientoSolicitudes],
})
export class NuevaSolicitudComponent implements OnInit, OnDestroy {
  private static readonly TODAS: Opcion[] = [
    {
      id: "ticket",
      // Una tarjeta por cada tipo de ticket, al mismo nivel que IA y Mantenimiento.
      tarjetas: TIPOS_TICKET.map(({ id, ...tipo }) => ({ ...tipo, idTicket: id })),
      icono: "support_agent",
      factory: "/TICKETS/Cte",
      mias: { texto: "Mis tickets", ruta: ["/TICKETS/Cte"] },
    },
    {
      id: "ia",
      tarjetas: [
        {
          tono: "ia",
          titulo: "Herramienta de IA",
          descripcion: "Pide usar una IA nueva; se evalúa antes de autorizarla.",
          ejemplos: ["Una IA que aún no está autorizada", "Para qué la usarías", "Con qué datos trabajaría"],
          icono: "smart_toy",
          accion: "Solicitar IA",
        },
      ],
      icono: "smart_toy",
      factory: "admin/solicitudes-ia/solicitud",
      mias: { texto: "Mis solicitudes de IA", ayuda: "Las IA que has pedido y en qué van.", ver: "ia" },
    },
    {
      id: "mantenimiento",
      tarjetas: [
        {
          tono: "mantenimiento",
          titulo: "Mantenimiento",
          descripcion: "Algo del edificio o de las instalaciones.",
          ejemplos: ["Luz, plomería o aire", "Mobiliario y cerraduras", "Limpieza"],
          icono: "handyman",
          accion: "Pedir mantenimiento",
        },
      ],
      icono: "handyman",
      factory: "admin/mantenimiento/solicitud",
      mias: {
        texto: "Mis solicitudes de mantenimiento",
        ayuda: "Lo que has pedido del edificio y cómo va.",
        ver: "mantenimiento",
      },
    },
  ];

  readonly PASOS: { id: Paso; texto: string }[] = [
    { id: "tipo", texto: "¿Qué necesitas?" },
    { id: "form", texto: "Tu solicitud" },
    { id: "listo", texto: "Listo" },
  ];

  opciones: Opcion[] = [];
  /** Las tarjetas de «¿Qué necesitas?», todas al mismo nivel: los tipos de ticket, IA y Mantenimiento. */
  tarjetas: TarjetaVisible[] = [];
  paso: Paso = "tipo";
  /** El formulario del paso 2, o la lista que se está viendo. */
  formulario: Formulario | null = null;
  resultado: Resultado | null = null;
  /** Lo que dice el acceso de cada tipo en «¿Ya pediste algo?»: cómo va lo que uno ya pidió. */
  marcas: Record<TipoSolicitud, Marca> = { ticket: CONSULTANDO, ia: CONSULTANDO, mantenimiento: CONSULTANDO };
  /** La solicitud de IA que la URL pide traer a la vista (?ver=ia&solicitud=42): así llega el aviso «espera tu respuesta». */
  destacadaIa: number | null = null;

  /** Mantenimiento de módulos (16/10/2026): las tarjetas en mantenimiento lo dicen y no abren. */
  private readonly mantenimiento = inject(EstadoMantenimientoService);

  /** Los formularios del paso 2, para poner sus botones en el encabezado. */
  private readonly formularioIa = viewChild(SolicitudIaComponent);
  private readonly formularioMantenimiento = viewChild(FormMantenimientoComponent);

  private readonly subs = new Subscription();
  /** La consulta del seguimiento de cada tipo, la última que se lanzó. */
  private readonly consultas = new Map<TipoSolicitud, Subscription>();
  /** Lo último que dijo la URL: se vuelve a leer si cambia el menú. */
  private url: ParamMap = convertToParamMap({});

  constructor(
    private readonly authService: AuthService,
    private readonly tickets: LanzadorTickets,
    private readonly seguimiento: SeguimientoSolicitudes,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.ponOpciones(this.authService.getMenuList() || []);
    // El menú se vuelve a pedir al servidor al cargar: si cambia (le
    // dieron o quitaron un permiso), las tarjetas se ajustan sin F5.
    this.subs.add(
      this.authService.menuCambiado.subscribe((menus: Menu[]) => {
        this.ponOpciones(menus || []);
        this.leeUrl(this.url);
        this.cdr.detectChanges();
      }),
    );
    this.subs.add(
      this.route.queryParamMap.subscribe((q) => {
        this.url = q;
        this.leeUrl(q);
        this.cdr.detectChanges();
      }),
    );
    // Lo que está en mantenimiento (16/10/2026): si el formulario abierto quedó en mantenimiento, de vuelta a las tarjetas.
    this.subs.add(
      this.mantenimiento.asegurar().subscribe(() => {
        if (this.paso === "form" && this.formulario && this.enMantenimiento(this.formulario)) {
          this.leeUrl(this.url);
        }
        this.cdr.detectChanges();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Lo que el usuario puede pedir según su menú, y las tarjetas que le tocan. */
  private ponOpciones(menus: Menu[]): void {
    this.opciones = NuevaSolicitudComponent.TODAS.filter((o) => tieneAccesoMenu(menus, o.factory));
    this.tarjetas = this.opciones.flatMap((o) => o.tarjetas.map((t) => ({ ...t, tipo: o.id })));
  }

  private tiene(id: TipoSolicitud): boolean {
    return this.opciones.some((o) => o.id === id);
  }

  private esFormulario(valor: string | null): valor is Formulario {
    return valor === "ia" || valor === "mantenimiento";
  }

  /**
   * La URL manda: ?ver= es la lista de lo pedido, ?tipo= el formulario de
   * ese tipo. Sin ninguno (o sin permiso para ese tipo, o con el ?tipo=ticket
   * de cuando los tipos de ticket iban en un paso aparte) se ven las tarjetas, salvo
   * en «Listo» mientras la URL siga en su ?enviada=: un F5 ahí (el
   * resultado ya no está en memoria) o un «atrás» regresan a las tarjetas.
   */
  private leeUrl(q: ParamMap): void {
    const ver = q.get("ver");
    const tipo = q.get("tipo");
    if (this.esFormulario(ver) && this.tiene(ver)) {
      this.ponPaso("mias", ver);
    } else if (this.esFormulario(tipo) && this.tiene(tipo) && !this.enMantenimiento(tipo)) {
      this.ponPaso("form", tipo);
    } else if (!(this.paso === "listo" && q.has("enviada"))) {
      this.ponPaso("tipo", null);
    }
    this.destacadaIa = this.paso === "mias" && this.formulario === "ia" ? Number(q.get("solicitud")) || null : null;
  }

  private ponPaso(paso: Paso, formulario: Formulario | null): void {
    // Al volver a las tarjetas se pregunta otra vez cómo va cada cosa: pudo haberse pedido otra.
    const regresa = paso === "tipo" && this.paso !== "tipo";
    this.paso = paso;
    this.formulario = formulario;
    if (paso !== "listo") {
      this.resultado = null;
    }
    if (paso === "tipo") {
      // Con las tarjetas de ticket a la vista, el lanzador pide sus catálogos, como al abrirse el diálogo de Tickets.
      if (this.tiene("ticket")) {
        this.tickets.prepara();
      }
      this.sigue(regresa);
    }
  }

  /**
   * Pregunta cómo va lo ya pedido de cada tipo que el usuario puede pedir.
   * Con `otraVez`, también de los que ya se sabía: mientras llega la
   * respuesta se queda lo anterior, sin parpadeo.
   */
  private sigue(otraVez: boolean): void {
    for (const { id } of this.opciones) {
      if (this.consultas.has(id) && !otraVez) {
        continue;
      }
      this.consultas.get(id)?.unsubscribe();
      const consulta = this.seguimiento.de(id).subscribe((s) => {
        this.marcas[id] = marca(id, s);
        this.cdr.detectChanges();
      });
      this.consultas.set(id, consulta);
      this.subs.add(consulta);
    }
  }

  /** Para el avance de arriba: el formulario de cualquier tipo es el paso 2. */
  indice(p: Paso = this.paso): number {
    return this.PASOS.findIndex((x) => x.id === p);
  }

  /**
   * El encabezado (15/10/2026, «que se muestre el nombre, Mantenimiento…»):
   * en un formulario y en «Listo», el tipo que se pide, con su icono y su
   * descripción; en la lista de lo pedido, esa lista; en las tarjetas,
   * «Solicitudes».
   */
  get encabezado(): { icono: string; titulo: string; ayuda: string } {
    const vista = this.vista;
    if (vista) {
      return { icono: vista.icono, titulo: vista.mias.texto, ayuda: vista.mias.ayuda || AYUDA_INICIO };
    }
    let tipo: Formulario | null = null;
    if (this.paso === "form") {
      tipo = this.formulario;
    } else if (this.paso === "listo") {
      tipo = this.resultado?.tipo ?? null;
    }
    const tarjeta = NuevaSolicitudComponent.TODAS.find((o) => o.id === tipo)?.tarjetas[0];
    if (tarjeta) {
      return { icono: tarjeta.icono, titulo: tarjeta.titulo, ayuda: tarjeta.descripcion };
    }
    return { icono: "add_circle", titulo: "Solicitudes", ayuda: AYUDA_INICIO };
  }

  /** En la lista de lo pedido, la opción de ese tipo (título, ayuda y botones del encabezado). */
  get vista(): Opcion | null {
    return (this.paso === "mias" && NuevaSolicitudComponent.TODAS.find((o) => o.id === this.formulario)) || null;
  }

  /**
   * En el paso 2, los botones del formulario de IA o de Mantenimiento: van
   * arriba, en el encabezado, como en Capacitación (15/10/2026). Las
   * consultas son señales: cuando el formulario aparece, el encabezado se
   * vuelve a pintar con sus botones.
   */
  get accionesFormulario(): TemplateRef<unknown> | null {
    if (this.paso !== "form") {
      return null;
    }
    const formulario = this.formulario === "ia" ? this.formularioIa() : this.formularioMantenimiento();
    return formulario?.acciones ?? null;
  }

  /** La pestaña que se le pide al componente de IA. */
  get pestanaIa(): PestanaSolicitudIa {
    return this.paso === "mias" ? "mias" : "solicitar";
  }

  /** El botón del encabezado de una lista: pedir otra de ese tipo. */
  elegir(o: Opcion): void {
    if (this.esFormulario(o.id)) {
      this.pedir(o.id);
    }
  }

  /**
   * Una tarjeta. Las de ticket abren SU formulario de siempre (un diálogo, o
   * su página en el módulo de Tickets); IA y Mantenimiento, su formulario
   * aquí mismo.
   */
  abre(t: TarjetaVisible): void {
    const enMantenimiento = this.mantenimientoDe(t);
    if (enMantenimiento) {
      this.avisaMantenimiento(enMantenimiento);
      return;
    }
    if (t.idTicket) {
      this.tickets.abre(t.idTicket);
    } else if (this.esFormulario(t.tipo)) {
      this.pedir(t.tipo);
    }
  }

  /** El paso 2: el formulario de IA o de Mantenimiento, aquí mismo. */
  pedir(tipo: Formulario): void {
    const enMantenimiento = this.enMantenimiento(tipo);
    if (enMantenimiento) {
      this.avisaMantenimiento(enMantenimiento);
      return;
    }
    this.ponPaso("form", tipo);
    this.marcaUrl({ tipo });
  }

  /**
   * Si la tarjeta está en mantenimiento (no la abre nadie, tampoco quien
   * administra el mantenimiento): los tickets por su tipo; IA y
   * Mantenimiento por su hoja del menú.
   */
  mantenimientoDe(t: TarjetaVisible): ModuloEnMantenimiento | undefined {
    if (t.idTicket) {
      const modulo = this.mantenimiento.porClave(claveTicket(t.idTicket));
      return this.mantenimiento.bloquea(modulo) ? modulo : undefined;
    }
    return this.esFormulario(t.tipo) ? this.enMantenimiento(t.tipo) : undefined;
  }

  private enMantenimiento(tipo: Formulario): ModuloEnMantenimiento | undefined {
    const modulo = this.mantenimiento.porClave(tipo === "ia" ? CLAVE_IA : CLAVE_MANTENIMIENTO);
    return this.mantenimiento.bloquea(modulo) ? modulo : undefined;
  }

  private avisaMantenimiento(modulo: ModuloEnMantenimiento): void {
    swal.fire({ icon: "info", title: modulo.nombre + " está en mantenimiento", text: textoAviso(modulo), confirmButtonText: "Entendido" });
  }

  /** La lista de lo que uno ya pidió de un tipo, aquí mismo. */
  verMias(tipo: Formulario, idSolicitud?: number): void {
    this.ponPaso("mias", tipo);
    this.marcaUrl(idSolicitud ? { ver: tipo, solicitud: idSolicitud } : { ver: tipo });
  }

  volverATipo(): void {
    this.ponPaso("tipo", null);
    this.marcaUrl({});
  }

  /** El componente de IA cambió de pestaña desde dentro. */
  alCambiarPestanaIa(cual: PestanaSolicitudIa): void {
    if (cual === "mias") {
      this.verMias("ia");
    } else {
      this.pedir("ia");
    }
  }

  alEnviarMantenimiento(r: MantenimientoEnviado): void {
    this.listo({ tipo: "mantenimiento", idSolicitud: r.idSolicitud, folio: r.folio, archivos: 0, fotosFallidas: r.fotosFallidas || [] });
  }

  alEnviarIa(r: SolicitudIaEnviada): void {
    this.listo({ tipo: "ia", idSolicitud: r.idSolicitud, folio: r.folio, archivos: r.archivos, fotosFallidas: [] });
  }

  private listo(r: Resultado): void {
    this.ponPaso("listo", r.tipo);
    this.resultado = r;
    // Una URL distinta de la de las tarjetas: si fuera la misma, el router
    // ignoraría el primer «atrás» y el usuario se quedaría en «Listo».
    this.marcaUrl({ enviada: r.folio || r.idSolicitud }, true);
    this.cdr.detectChanges();
  }

  /** «Ver mi solicitud» desde «Listo»: la lista de su tipo (en Mantenimiento, con su detalle abierto). */
  verMiSolicitud(): void {
    const r = this.resultado;
    if (!r) {
      return;
    }
    this.verMias(r.tipo, r.tipo === "mantenimiento" ? r.idSolicitud : undefined);
  }

  /**
   * La URL dice en qué paso está, para que F5 o «atrás» no pierdan el
   * lugar. Entrar a un formulario o a una lista deja huella en el
   * historial («atrás» regresa a las tarjetas); «Listo» la reemplaza
   * por ?enviada=, así que desde ahí «atrás» también va a las tarjetas.
   */
  private marcaUrl(queryParams: Params, reemplaza = false): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: reemplaza,
    });
  }
}
